import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent, DialogConfig } from '@vsolv/vectors-ui/dialog';
import { Policy } from '@wsphere/warranties/domain';
import { PolicyService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-duplicate-plan-dialog',
  templateUrl: './duplicate-plan.dialog.html',
  standalone: false,
})
export class DuplicatePlanDialog {
  constructor(
    private router: Router,
    private toastSvc: ToastService,
    private policySvc: PolicyService,
    private formBuilder: FormBuilder
  ) {}

  @ViewChild(DialogComponent) dialog!: DialogComponent;

  @Input() policy: Policy.Model | null = null;
  @Input() plan: Policy.Plan.Model | null = null;
  @Input() dialogConfig?: DialogConfig;

  saving = false;
  draftStatus = Policy.Status.DRAFT;

  form = this.formBuilder.group({
    title: ['', [Validators.required]],
  });

  open() {
    this.form.reset({ title: this.plan ? `${this.plan.title} (Copy)` : '' });
    this.dialog.open();
  }

  close() {
    this.saving = false;
    this.dialog.close();
  }

  cancel() {
    this.close();
  }

  async duplicatePlan() {
    if (!this.policy) throw new Error('Unable to duplicate plan, policy not found');
    if (!this.plan) throw new Error('Unable to duplicate plan, plan not found');

    this.saving = true;

    const title = this.form.value.title || '';
    const policyId = this.policy.id;
    const planId = this.plan.id;

    const duplicatedPlan = await this.policySvc.duplicatePlan(policyId, planId, title).catch(err => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: this.getErrorMessage(err),
      });

      this.close();
    });

    if (!duplicatedPlan) return;

    // Toasts render their text as trusted HTML, so anything user provided has to be escaped
    const planTitle = this.escapeHtml(title);

    if (duplicatedPlan.policyId === policyId) {
      this.toastSvc.show({
        type: 'success',
        title: 'Duplicated plan',
        text: '<strong>' + planTitle + '</strong> has been successfully created.',
      });
    } else {
      this.toastSvc.show({
        type: 'success',
        title: 'New policy version created',
        text:
          '<strong>' +
          planTitle +
          '</strong> has been successfully created and a new version of <strong>' +
          this.escapeHtml(this.policy.title) +
          '</strong> has been created.',
      });
    }

    await Promise.all([
      this.policySvc.refreshPolicy(duplicatedPlan.policyId),
      this.policySvc.refreshPlan({ id: duplicatedPlan.id, policyId: duplicatedPlan.policyId } as Policy.Plan.Model, {
        includeCoverageConfigs: false,
        includeFees: false,
        includeCoverageConfigBlocks: false,
      }),
    ]);

    await this.router.navigate(['/policies/' + duplicatedPlan.policyId + '/plans/' + duplicatedPlan.id]);

    this.close();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getErrorMessage(err: any): string {
    const message = err?.error?.message ?? err?.message;
    return typeof message === 'string' && message.length ? message : 'An unexpected error occurred. Please try again.';
  }

  private escapeHtml(value: string): string {
    return value.replace(
      /[&<>"']/g,
      char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char
    );
  }
}
